import { toast as sonnerToast } from "sonner";
import { useCallback } from "react";
import type { ToastProps } from "@/types/toast.types";

const variantMap: Record<string, "success" | "error" | "info" | "warning" | "default"> = {
  destructive: "error",
  success: "success",
  error: "error",
  info: "info",
  warning: "warning",
  default: "default",
};

function toast(props: ToastProps | string) {
  const toastProps = typeof props === "string" ? { title: props } : props;

  const type = variantMap[toastProps.variant || "default"];

  const options: Record<string, unknown> = {
    description: toastProps.description,
    duration: toastProps.duration,
    dismissible: toastProps.dismissible,
    action: toastProps.action,
    cancel: {
      label: "Fechar",
      onClick: () => {},
    },
  };

  let toastId: string | number;
  if (type === "default") {
    toastId = sonnerToast(toastProps.title, options);
  } else {
    toastId = sonnerToast[type](toastProps.title, options);
  }

  return {
    id: String(toastId),
    dismiss: () => sonnerToast.dismiss(toastId),
    update: (updates: Partial<ToastProps>) => {
      const updatedProps = { ...toastProps, ...updates };
      const updatedType = variantMap[updatedProps.variant || "default"];
      const updateOptions = {
        ...options,
        description: updatedProps.description,
        duration: updatedProps.duration,
        id: toastId,
        cancel: {
          label: "Fechar",
          onClick: () => {},
        },
      };

      if (updatedType === "default") {
        sonnerToast(updatedProps.title, updateOptions);
      } else {
        sonnerToast[updatedType](updatedProps.title, updateOptions);
      }
    },
  };
}

function useToast() {
  const dismiss = useCallback((toastId?: string) => {
    if (toastId) {
      sonnerToast.dismiss(toastId);
    } else {
      sonnerToast.dismiss();
    }
  }, []);

  return {
    toast,
    dismiss,
  };
}

export { useToast, toast };
